import {
  Button,
  ButtonGroup,
  Input,
  ModalIcon,
  Spacer,
  Stack,
  TextBody,
  TextTitle,
} from '@pypestream/design-system';
import { FC, useEffect, useState } from 'react';

import { sendProStudioEvent } from '../../../xstate/app.xstate';
import { GenericModal, GenericModalProps } from '..';

type Env = 'live' | 'sandbox';

export type DeleteLiveAgentPlatformModalProps = GenericModalProps & {
  connectionId?: string;
  connectionName?: string;
  env?: Env;
};

export const DeleteLiveAgentPlatformModal: FC<
  DeleteLiveAgentPlatformModalProps
> = ({
  connectionId,
  connectionName = '',
  env = 'live',
  modalRef,
  onClose,
}) => {
  const [inputValue, setInputValue] = useState('');
  const [hasError, setHasError] = useState(false);

  // Clear the verification field whenever the modal targets a new connector.
  useEffect(() => {
    setInputValue('');
    setHasError(false);
  }, [connectionId, connectionName]);

  const handleClose = () => {
    setInputValue('');
    setHasError(false);
    onClose?.();
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setHasError(false);
    setInputValue(e.target.value);
  };

  const handleDelete = () => {
    if (!connectionId) return;

    if (inputValue !== connectionName) {
      setHasError(true);
      return;
    }

    sendProStudioEvent({
      type: 'deleteLiveAgentPlatform',
      payload: { id: connectionId, env, cb: handleClose },
    });
  };

  const isConfirmed = inputValue === connectionName && !!connectionName;

  return (
    <GenericModal
      test-id="delete-live-agent-platform-modal"
      modalRef={modalRef}
      onClose={handleClose}
    >
      <Stack justifyContent="center">
        <ModalIcon name="error" />
      </Stack>
      <TextTitle size="small" textAlign="center">
        Delete this Live Agent Platform Connector?
      </TextTitle>
      <Spacer size="small" />
      <TextBody variant="secondary" textAlign="center">
        Deleting this connector removes the escalation configuration from all
        channels assigned to it.
      </TextBody>
      <Spacer size="large" />
      <TextBody variant="secondary">
        To verify, type{' '}
        <span color="warning">&ldquo;{connectionName}&rdquo;</span> below
      </TextBody>
      <Spacer size="2xsmall" />
      <Input
        test-id="delete-live-agent-platform-modal-input"
        variant="outlined"
        value={inputValue}
        onChange={handleInputChange}
        // @ts-expect-error - DS Input forwards onInput with target.value
        onInput={handleInputChange}
        hasError={hasError}
        helpText={hasError ? 'Please verify the connection name.' : ''}
        name="connection-name"
      />
      <ButtonGroup slot="footer">
        <Button
          test-id="delete-live-agent-platform-modal-cancel"
          width="full"
          variant="secondary"
          size="large"
          onClick={handleClose}
        >
          Cancel
        </Button>
        <Button
          test-id="delete-live-agent-platform-modal-delete"
          width="full"
          variant="warning"
          size="large"
          disabled={!isConfirmed}
          onClick={handleDelete}
        >
          Delete
        </Button>
      </ButtonGroup>
    </GenericModal>
  );
};
